import { useTranslation } from 'react-i18next';

import type { RunProgress, RunRead, RunSummary } from '../types/run.api.types';
import { catalogLabel } from '../utils/catalog-labels';

import { useExperiment } from './use-experiment';

/** The run fields a title is built from — a full `RunRead`, or one adapted from a `RunSummary`. */
export type RunTitleSource = Pick<
  RunRead,
  'experimentId' | 'experimentName' | 'name' | 'flow' | 'config'
>;

/**
 * Adapts a summaries row to the title inputs `RunRead` gives, so both paths title a run alike.
 * `requestName` IS `RunRead.name` (the run's own name, even one equal to its flow code). Only an
 * older backend without it falls back to unpicking the RAW `name` precedence: kept unless it is
 * the flow code (which the title localises) or the experiment name ("<name> · run" wins anyway).
 */
export function titleSourceFromSummary(summary: RunSummary): RunTitleSource {
  const ownName =
    summary.requestName !== undefined
      ? summary.requestName
      : !summary.experimentName && summary.name !== summary.flow
        ? summary.name
        : null;
  return {
    experimentId: summary.experimentId,
    experimentName: summary.experimentName,
    name: ownName,
    flow: summary.flow,
  };
}

/**
 * The name a run goes by (Q24): "<experiment name> · run" when an experiment started it,
 * else the run's own name, else its flow. Shared by the run header and the breadcrumb so
 * both always say the same thing. The name comes from the run read itself (§75
 * `experimentName`); only an older deploy that omits it falls back to the cached experiment read.
 */
export function useRunTitle(run: RunTitleSource | undefined, progress: RunProgress | undefined) {
  const { t } = useTranslation('catalog-enrichment-run');
  const { t: tCatalog } = useTranslation('catalog-enrichment');
  const experimentId = progress?.experimentId ?? run?.experimentId ?? null;
  // The server-resolved name, when it names the same experiment: no per-run experiment read
  // (a runs list page would otherwise fetch one experiment per row).
  const servedName = run?.experimentId === experimentId ? (run.experimentName ?? null) : null;
  const { data: experiment } = useExperiment(servedName ? undefined : (experimentId ?? undefined));
  const experimentName = servedName ?? experiment?.name ?? null;
  const flow = progress?.flow ?? run?.flow ?? run?.config?.flow?.name ?? null;

  const title = experimentName
    ? t('header.experimentRunTitle', { name: experimentName })
    : (run?.name ?? (flow ? catalogLabel(tCatalog, 'flow', flow) : t('header.untitled')));
  return { title, experimentId, experimentName };
}
